/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { mergeStyles } from '@cian/utils';
import {
  IAgentAvailabilitySchema,
  IAgentSchema,
  IKPSchema,
  IOfferSchema,
  IPhoneSchema,
  IUserSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { IOfferChatsState } from '../../store/offer_chats';
import { getMainContactsDescription } from '../../utils/getMainContactsDescription';
import { SimpleBotDetector } from '../../utils/simpleBotDetector';
import * as AnalyticsAside from '../contacts_aside/tracking';
import { PopupOwnOfferContainer } from '../popups/own_offer/container';
import { PopupStartChatContainer } from '../popups/start_chat/container';
import * as styles from './index.css';
import * as Analytics from './tracking';
import { ABCallButton } from '../ab_call_button';
import { DynamicCalltrackingButton, EPageTypes, EPlatformTypes } from '@cian/frontend-dynamic-calltracking-component';
import { formatNumber } from '../../utils/phone';
import { CalltrackingBadgeContainer } from '../../containers/CalltrackingBadge';
import { CallSourceWishContainer } from '../../containers/CallSourceWish';
import { ERequestDemandExperiment } from '../../utils/newRequestDemandPositionExperiment';

export interface IContactsMainStoreProps {
  abGroup: number;
  requestDemandPosition?: ERequestDemandExperiment;
  offer: IOfferSchema;
  agent: IAgentSchema | null;
  offerChat: IOfferChatsState;
  user: IUserSchema | null;
  agentAvailability: IAgentAvailabilitySchema | null;
  kp: IKPSchema | null;
  siteBlockId: number | null;
  newbuildingId: number | null;
  isAuthenticated: boolean;
  isLeadFactory: boolean;
}

export interface IContactsMainState {
  isChatMessageSent: boolean;
  isOwnOfferPopupOpen: boolean;
  isPhonesOpened: boolean;
  isStartChatPopupOpen: boolean;
  startChatMessage?: string;
}

export interface IContactsMainDispatchProps {
  setPhoneCollapse(): void;

  chatMessageSent(): void;
}

type IContactsMainProps = IContactsMainStoreProps & IContactsMainDispatchProps;

export class ContactsMain extends React.Component<IContactsMainProps, IContactsMainState> {
  public state: IContactsMainState = {
    isChatMessageSent: false,
    isOwnOfferPopupOpen: false,
    isPhonesOpened: false,
    isStartChatPopupOpen: false,
  };
  private botDetector = new SimpleBotDetector();

  public componentDidMount() {
    this.botDetector.init();
  }

  public render() {
    const { user, agent, offer, agentAvailability, kp, siteBlockId, isLeadFactory, requestDemandPosition } = this.props;
    const { isStartChatPopupOpen, isOwnOfferPopupOpen, startChatMessage, isPhonesOpened } = this.state;
    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
    const { phones } = this.props.offer;

    const phonesAvailable = phones && !phones.some(phone => !phone.countryCode || !phone.number);

    // Если обяъвление загородки привязано к коттеджному поселку,
    // проверяем возможность отображения телефонов у застройщика.
    const shouldRenderContactButtons = kp ? kp.displayBuilderPhone : true;

    if (!phonesAvailable || !shouldRenderContactButtons) {
      return null;
    }

    const withBottomSpace = isLeadFactory && isPhonesOpened;
    const formattedPhones = (phones as IPhoneSchema[]).map(formatNumber).join(', ');
    const isDemandBExperiment = requestDemandPosition === ERequestDemandExperiment.B;
    const isDemandCExperiment = requestDemandPosition === ERequestDemandExperiment.C;

    return (
      <div
        {...mergeStyles(
          styles['container'],
          withBottomSpace && styles['container--bottom_space'],
          isDemandBExperiment && styles['container--experiment-b'],
          isDemandCExperiment && styles['container--experiment-c'],
        )}
      >
        {!isDemandBExperiment && <div className={styles['title']}>Узнайте больше</div>}

        <div className={styles['description']}>{getMainContactsDescription(offer)}</div>

        <DynamicCalltrackingButton
          className={styles['button']}
          openPhoneClassName={styles['phones']}
          isOpen={isPhonesOpened}
          pageType={EPageTypes.OfferCard}
          platformType={EPlatformTypes.WebDesktop}
          placeType={'ContactsMain'}
          phone={formattedPhones}
          announcementId={offer.id}
          blockId={siteBlockId}
          onClick={() => {
            this.openPhones();
            this.startChat();
          }}
        >
          <ABCallButton />
        </DynamicCalltrackingButton>
        {isPhonesOpened && <CalltrackingBadgeContainer />}
        {isPhonesOpened && (
          <div className={styles['sign']}>
            <CallSourceWishContainer />
          </div>
        )}

        <PopupStartChatContainer
          isOpen={isStartChatPopupOpen}
          message={startChatMessage}
          onMessageChange={message => this.setState({ startChatMessage: message })}
          onMessageSent={chatId => {
            this.props.chatMessageSent();
            if (user && isAgentNotAvailable && agent) {
              AnalyticsAside.trackAgentAvailabilityMessageSent(offer, agent, user);
            } else {
              AnalyticsAside.trackFirstMessageSent(agent, offer, user, kp && kp.id);
            }
          }}
          onClose={() => this.setState({ isStartChatPopupOpen: false })}
        />
        <PopupOwnOfferContainer
          isOpen={isOwnOfferPopupOpen}
          onClose={() => this.setState({ isOwnOfferPopupOpen: false })}
        />
      </div>
    );
  }

  private openPhones = async () => {
    const { abGroup, offer, agent, setPhoneCollapse, user, kp } = this.props;

    Analytics.trackOpenPhonesClick(offer, agent, user, abGroup, kp && kp.id, !this.botDetector.isHuman());
    setPhoneCollapse();
    this.setState({
      isPhonesOpened: true,
    });
  };

  private startChat = () => {
    const {
      offer,
      agent,
      user,
      agentAvailability,
      offerChat: { isChatsEnabled },
    } = this.props;

    const isUserAuthenticated = Boolean(user && user.isAuthenticated);
    const isOfferOwnedByUser = Boolean(user && user.isAuthenticated && user.realtyUserId === offer.userId);
    const isAgentNotAvailable = Boolean(agentAvailability && !agentAvailability.available);
    const canShowAvailability = isChatsEnabled && isAgentNotAvailable;

    if (!canShowAvailability) {
      return;
    }

    switch (true) {
      case !isUserAuthenticated:
        return this.callLoginPopup();
      case isOfferOwnedByUser:
        return this.openOwnPopup();
      default:
        AnalyticsAside.trackFirstMessageShowPopup(offer, agent, 'open_phone');

        return this.openStartChatPopup();
    }
  };

  private openStartChatPopup() {
    this.setState({ isStartChatPopupOpen: true });
  }

  private callLoginPopup = () => {
    //tslint:disable-next-line:no-any
    if ((window as any).waitForLoginPopup) {
      (window as any).waitForLoginPopup(); //tslint:disable-line:no-any
    }
  };

  private openOwnPopup() {
    this.setState({ isOwnOfferPopupOpen: true });
  }
}
